import { ChangeEvent, FC } from 'react';
import {
    Box,
    FormHelperText,
    InputLabel,
    MenuItem,
    Select,
    SelectChangeEvent,
    TextField,
    Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';

import { defaultMonthOption } from 'src/components/date-of-birth-input/constants';

import { DateOfBirthInputProps } from './types';
import { styles } from './styles';
import { months } from './constants';

export const DateOfBirthInput: FC<DateOfBirthInputProps> = ({
    label,
    value = { day: '', month: defaultMonthOption.value, year: '' },
    onChange,
    error = false,
    helperText,
}) => {
    const { t } = useTranslation();

    const handleChangeDay = (e: ChangeEvent<HTMLInputElement>) => {
        const { target } = e;
        const numberValue = +target.value;

        if (Number.isNaN(numberValue)) return;

        onChange({
            ...value,
            day: target.value,
        });
    };

    const handleChangeMonth = (e: SelectChangeEvent<string | null>) => {
        const { target } = e;
        onChange({
            ...value,
            month: target.value,
        });
    };

    const handleChangeYear = (e: ChangeEvent<HTMLInputElement>) => {
        const { target } = e;
        const numberValue = +target.value;

        if (Number.isNaN(numberValue)) return;

        onChange({
            ...value,
            year: target.value,
        });
    };

    const handleYearBlur = () => {
        const yearValue = value.year || '';
        if (!/^\d{4}$/.test(yearValue)) {
            onChange({
                ...value,
                year: '',
            });
        }
    };

    return (
        <Box css={styles.container}>
            <InputLabel shrink>{label}</InputLabel>
            <Box css={styles.inputWrapper}>
                <TextField
                    type="text"
                    inputMode="numeric"
                    value={value.day}
                    onChange={handleChangeDay}
                    variant="standard"
                    placeholder={t('your_details_form_date_of_birth_day_placeholder')}
                    css={styles.input}
                    fullWidth
                    error={error}
                    inputProps={{ pattern: '[0-9]*' }}
                />

                <Select
                    value={value.month || defaultMonthOption.value}
                    onChange={handleChangeMonth}
                    variant="standard"
                    fullWidth
                    css={styles.select}
                    error={error}
                    displayEmpty
                    id="dob-month-input"
                >
                    <MenuItem value={defaultMonthOption.value}>
                        <Typography variant="body1">{t(defaultMonthOption.label)}</Typography>
                    </MenuItem>
                    {months.map((month) => (
                        <MenuItem key={month.value} value={month.value}>
                            <Typography variant="body1">{t(month.label)}</Typography>
                        </MenuItem>
                    ))}
                </Select>

                <TextField
                    type="text"
                    inputMode="numeric"
                    value={value.year}
                    onChange={handleChangeYear}
                    onBlur={handleYearBlur}
                    variant="standard"
                    css={styles.input}
                    fullWidth
                    error={error}
                    placeholder={t('your_details_form_date_of_birth_year_placeholder')}
                    inputProps={{ pattern: '[0-9]*' }}
                />
            </Box>
            {error && helperText && (
                <FormHelperText variant="standard" error>
                    <Typography
                        variant="body2"
                        css={styles.link}
                        dangerouslySetInnerHTML={{ __html: t(helperText) }}
                    />
                </FormHelperText>
            )}
        </Box>
    );
};
